<script setup lang="ts">
import Logo from '@/assets/logos/logo.svg'
import IconSuiteCase from '@/assets/icons/suitcase-lg.svg'
import IconSearch from '@/assets/icons/search.svg'
import IconCalendar from '@/assets/icons/calendar.svg'
import Map from '@/assets/icons/map.svg'
import Bell from '@/assets/icons/bell.svg'
import { useUserNotificationStore } from '@/stores/useUserNotificationStore'
import NavbarButton from '@/components/common/layout/NavbarButton.vue'
import MobileBottomNavigation from './MobileBottomNavigation.vue'
import IconPen from '@/assets/icons/pen.svg'
import PictoBs from '@/assets/icons/picto-bs.svg'
import PictoBs1 from '@/assets/icons/logo-boostsales.svg'
import {
  ArrowLeftIcon,
  ArrowRightIcon,
  LifebuoyIcon,
  ArrowLeftStartOnRectangleIcon,
  ChartBarSquareIcon,
  CurrencyDollarIcon,
  SignalIcon,
  UsersIcon,
  UserGroupIcon,
  ListBulletIcon,
  Cog8ToothIcon,
  ChatBubbleLeftRightIcon,
  TruckIcon,
  PresentationChartLineIcon,
} from '@heroicons/vue/24/outline'
import ChatSquareText from '@/assets/icons/chat-square-text.svg'
import { useNavBarStore } from '@/stores/navBarStore'
import { useBoostCorePermissionStore } from '@/stores/useBoostCorePermissionStore'
import { useUserPreferenceStore } from '@/stores/useUserPreferenceStore'
import { storeToRefs } from 'pinia'
import { useRouter } from 'vue-router'
import { computed, nextTick, onBeforeUnmount, onMounted, ref, watch } from 'vue'
import { useNewsStore } from '@/stores/useNewsStore'
import { DocumentCurrencyEuroIcon } from '@heroicons/vue/16/solid'
import UserPreferenceModal from '@/components/features/user/UserPreferenceModal.vue'
import Avatar from '@/components/common/ui/Avatar.vue'

const navBarStore = useNavBarStore()
const boostCoreStore = useBoostCorePermissionStore()
const { drawerOpen } = storeToRefs(navBarStore)

// `drawerOpen` ne pilote le repli (icônes-only) que sur desktop (lg+).
// Sous lg, le menu est un slide-out : il doit toujours afficher les libellés.
const isDesktop = ref(false)
let mediaQuery: MediaQueryList | null = null
const updateIsDesktop = () => {
  isDesktop.value = mediaQuery?.matches ?? window.innerWidth >= 1024
}
const collapsed = computed(() => isDesktop.value && !drawerOpen.value)

const { user } = storeToRefs(boostCoreStore)
const userEmail = computed(() => user.value?.email ?? undefined)
const openModalPreferences = ref(false)

const newsStore = useNewsStore()
const notificationStore = useUserNotificationStore()
const userPreferenceStore = useUserPreferenceStore()
const router = useRouter()

const { unreadCount } = storeToRefs(notificationStore)

onMounted(() => {
  if (boostCoreStore.user) {
    notificationStore.fetchUnreadCount()
    userPreferenceStore.fetchPreferences()
  }
})

watch(() => boostCoreStore.user, (u) => {
  if (u) {
    notificationStore.fetchUnreadCount()
    userPreferenceStore.fetchPreferences()
  }
})

const userFullName = computed(() => {
  const u = user.value
  if (!u) return ''
  const parts = [u.firstName, u.lastName].filter(Boolean)
  return parts.join(' ') || u.email || ''
})

const drawerSideRef = ref<HTMLElement | null>(null)
const navScrollRef = ref<HTMLElement | null>(null)
const showScrollShadowTop = ref(false)
const showScrollShadowBottom = ref(false)

const SCROLL_THRESHOLD = 4

function checkScrollShadow() {
  const el = navScrollRef.value
  if (!el) return
  const { scrollTop, scrollHeight, clientHeight } = el
  const canScroll = scrollHeight > clientHeight
  const atTop = scrollTop <= SCROLL_THRESHOLD
  const atBottom = scrollTop + clientHeight >= scrollHeight - SCROLL_THRESHOLD
  showScrollShadowTop.value = canScroll && !atTop
  showScrollShadowBottom.value = canScroll && !atBottom
}

const toggleBtnPos = ref({ left: 0, bottom: 0 })

function updateToggleBtnPos() {
  const el = drawerSideRef.value
  if (!el) return
  const rect = el.getBoundingClientRect()
  toggleBtnPos.value = {
    left: rect.right + 3,
    bottom: window.innerHeight - rect.bottom + 32,
  }
}

const userMenuOpen = ref(false)
const userMenuTriggerRef = ref<HTMLElement | null>(null)
const userMenuPos = ref({ left: 0, bottom: 0 })

function updateUserMenuPos() {
  const el = userMenuTriggerRef.value
  if (!el) return
  const rect = el.getBoundingClientRect()
  userMenuPos.value = {
    left: rect.left,
    bottom: window.innerHeight - rect.top + 8,
  }
}

function toggleUserMenu() {
  userMenuOpen.value = !userMenuOpen.value
  if (userMenuOpen.value) nextTick(() => updateUserMenuPos())
}

function closeUserMenu() {
  userMenuOpen.value = false
}

function openPreferences() {
  userPreferenceStore.fetchPreferences()
  openModalPreferences.value = true
}
function closeModalPreferences() {
  openModalPreferences.value = false
}
let clickOutsideHandler: ((e: MouseEvent) => void) | null = null
watch(userMenuOpen, (open) => {
  if (open) {
    updateUserMenuPos()
    clickOutsideHandler = (e: MouseEvent) => {
      const target = e.target as Node
      const menu = document.getElementById('user-menu-dropdown')
      if (!userMenuTriggerRef.value?.contains(target) && !menu?.contains(target)) {
        closeUserMenu()
        document.removeEventListener('click', clickOutsideHandler!)
        clickOutsideHandler = null
      }
    }
    requestAnimationFrame(() => document.addEventListener('click', clickOutsideHandler!))
  } else if (clickOutsideHandler) {
    document.removeEventListener('click', clickOutsideHandler)
    clickOutsideHandler = null
  }
})

let resizeObserver: ResizeObserver | null = null
let observedEl: HTMLElement | null = null
onMounted(() => {
  newsStore.checkForNews()
  mediaQuery = window.matchMedia('(min-width: 1024px)')
  updateIsDesktop()
  mediaQuery.addEventListener('change', updateIsDesktop)
  nextTick(() => {
    updateToggleBtnPos()
    checkScrollShadow()
  })
  window.addEventListener('resize', updateToggleBtnPos)
  resizeObserver = new ResizeObserver(() => {
    updateToggleBtnPos()
    nextTick(checkScrollShadow)
  })
  nextTick(() => {
    observedEl = drawerSideRef.value
    if (observedEl) resizeObserver?.observe(observedEl)
    if (navScrollRef.value) resizeObserver?.observe(navScrollRef.value)
  })
})
onBeforeUnmount(() => {
  mediaQuery?.removeEventListener('change', updateIsDesktop)
  window.removeEventListener('resize', updateToggleBtnPos)
  if (observedEl && resizeObserver) resizeObserver.unobserve(observedEl)
  if (navScrollRef.value && resizeObserver) resizeObserver.unobserve(navScrollRef.value)
  resizeObserver?.disconnect()
})
const DRAWER_TRANSITION_MS = 320
watch(drawerOpen, () => {
  nextTick(() => {
    updateToggleBtnPos()
    checkScrollShadow()
  })
  setTimeout(() => {
    updateToggleBtnPos()
    checkScrollShadow()
  }, DRAWER_TRANSITION_MS)
})

const logout = () => {
  router.push('/auth/logout')
}

const openNewsFeed = () => {
  router.push('/news')
}

// computed : l'URL "Devis" (Odoo) dépend de la source résolue par boostcore pour l'user
const navItems = computed(() => [
  {
    label: 'Recherche',
    url: '/search',
    svg: IconSearch
  },
  {
    label: 'Agenda',
    url: '/diary',
    svg: IconCalendar
  },
  {
    label: 'Notifications',
    url: '/notifications',
    svg: Bell
  },
  {
    label: 'Affaires',
    url: '/opportunity',
    svg: IconSuiteCase
  },
  {
    label: 'Campagnes',
    url: '/campaigns',
    svg: Map
  },
  {
    label: 'To Do',
    url: '/dashboard/todos',
    svg: ListBulletIcon
  },
  {
    label: 'Mes clients',
    url: '/commercial/dashboard',
    svg: PresentationChartLineIcon
  },
  {
    label: 'Kpi',
    url: '/kpi',
    svg: ChartBarSquareIcon
  },
  {
    label: 'Éligibilités',
    url: '/eligibility',
    svg: SignalIcon
  },
  {
    label: 'Commandes',
    url: '/dashboard/orders',
    svg: DocumentCurrencyEuroIcon
  },
  {
    label: 'Variables',
    url: '/variables',
    svg: CurrencyDollarIcon
  },
  {
    label: 'Livraisons',
    url: '/dashboard/admin/deliveries',
    svg: TruckIcon
  },
  {
    label: 'Admin utilisateurs',
    url: '/admin/users',
    svg: UsersIcon
  },
  {
    label: 'Support interne',
    url: '/support-interne',
    svg: ChatSquareText
  },
  {
    label: 'Devis',
    url: boostCoreStore.odooQuotesUrl,
    svg: IconPen,
    externe: true
  },
  {
    label: 'Contacts',
    url: '/dashboard/contacts',
    svg: UserGroupIcon
  },
  {
    label: 'Leads',
    url: '/dashboard/leads',
    svg: ChatBubbleLeftRightIcon
  },
  {
    label: 'Stats H3A',
    url: '/h3a-stats',
    svg: PresentationChartLineIcon
  },
  {
    label: 'BoostSales',
    url: 'https://boost-sales.groupe-pandora.com/',
    svg: PictoBs1,
    externe: true
  },
  {
    label: 'Aide',
    url: 'https://doc.groupe-pandora.com/books/aide',
    svg: LifebuoyIcon,
    externe: true
  },
])
const visibleNavItems = computed(() =>
  navItems.value.filter((item) => {
    if (item.url === '/dashboard/orders') return boostCoreStore.canAccessOrders
    if (item.url === '/variables') return boostCoreStore.canAccessVariables
    if (item.url === '/eligibility') return boostCoreStore.canAccessEligibility
    if (item.url === '/dashboard/admin/deliveries') return boostCoreStore.can('DELIVERY_ADMIN') || boostCoreStore.include('SUPER_ADMIN')
    if (item.url === '/admin/users') return boostCoreStore.include('USER_MASTER')
    if (item.url === '/support-interne') return boostCoreStore.include('SUPER_ADMIN')
    if (item.url === '/dashboard/leads') return boostCoreStore.canAccessLeads
    if (item.url === '/h3a-stats') return boostCoreStore.can('H3A_STATS')
    if (item.url === '/commercial/dashboard') return boostCoreStore.can('SALES_READ')
    return true
  }),
)
watch(visibleNavItems, () => nextTick(checkScrollShadow), { deep: true })
</script>
<template>
  <div ref="drawerSideRef" class="drawer-side" :class="{ 'overflow-visible': collapsed }">
    <!-- L'overlay DOIT précéder le menu : tous les enfants de .drawer-side sont
         empilés dans la même cellule de grille, l'ordre DOM décide du z-order.
         S'il est après le menu, il passe au-dessus et intercepte les clics. -->
    <label for="my-drawer-2" aria-label="close sidebar" class="drawer-overlay lg:hidden"></label>
    <ul class="menu bg-base-100 text-base-content flex flex-col h-full min-h-0 p-2 pr-0" :class="{ 'close-bar': collapsed }">
      <div class="shrink-0 flex items-center justify-center my-4 h-10">
        <RouterLink to="/" class="flex items-center justify-center space-x-2">
          <component v-if="!collapsed" :is="Logo" class="w-48 h-auto" alt="logo" />
          <component v-else :is="PictoBs" class="w-10 h-auto" alt="logo" />
        </RouterLink>
      </div>
      <!-- Wrapper non scrollable -->
      <div class="relative flex-1 min-h-0">
        <div
          ref="navScrollRef"
          class="h-full flex flex-col hide-scrollbar"
          :class="{ 'w-fit overflow-x-visible': collapsed, ' overflow-y-auto': !collapsed }"
          @scroll="checkScrollShadow"
        >
          <div
            v-for="(item, index) in visibleNavItems"
            :key="index"
            :class="{
              'tooltip tooltip-right': collapsed,
            }"
            :data-tip="item.label"
          >
            <NavbarButton
              :class="{ 'w-fit': collapsed }"
              :key="index"
              :label="item.label"
              :url="item.url"
              :externe="item.externe || false"
              :drawerOpen="!collapsed"
            >
              <div class="w-10 h-10 flex justify-center items-center mx-2 relative">
                <component :is="item.svg" class="w-4" />
                <div v-if="item.url === '/notifications' && unreadCount > 0"
                     class="absolute top-1 right-1 w-4 h-4 bg-error text-white text-[10px] flex items-center justify-center rounded-full">
                  {{ unreadCount > 9 ? '9+' : unreadCount }}
                </div>
              </div>
            </NavbarButton>
          </div>
        </div>
        <!-- SHADOW TOP -->
        <div
          v-show="showScrollShadowTop"
          class="pointer-events-none absolute top-0 left-0 right-0 h-8 bg-gradient-to-b from-black/15 to-transparent z-10"
        />

        <!-- SHADOW BOTTOM -->
        <div
          v-show="showScrollShadowBottom"
          class="pointer-events-none absolute bottom-0 left-0 right-0 h-8 bg-gradient-to-t from-black/15 to-transparent z-10"
        />
      </div>

      <div
        class="z-10 shrink-0 flex items-center rounded-s-full transition-all duration-300 py-4 w-full"
        :class="!collapsed ? 'justify-start' : 'justify-center'"
      >
        <button
          ref="userMenuTriggerRef"
          type="button"
          class="btn btn-ghost flex items-center p-0 min-h-0 h-auto rounded-s-full cursor-pointer w-full"
          :class="{ 'w-fit justify-center': collapsed }"
          @click="toggleUserMenu"
        >
          <div class="w-10 h-10 flex justify-center items-center shrink-0" :class="!collapsed ? 'mx-2' : 'mx-0'">
            <Avatar :email="userEmail" size="48" />
          </div>
          <span v-if="!collapsed && userFullName" class="text-nowrap truncate text-sm">{{ userFullName }}</span>
        </button>
        <Teleport to="body">
          <div
            v-show="userMenuOpen"
            id="user-menu-dropdown"
            class="fixed z-99999 menu p-1.5 shadow-lg bg-base-100 rounded-box w-44 border border-base-300"
            :style="{ left: userMenuPos.left + 'px', bottom: userMenuPos.bottom + 'px' }"
          >
            <div class="px-2 py-1.5 border-b border-base-300">
              <p class="text-[10px] font-semibold text-base-content/70 uppercase">À propos</p>
              <div class="flex items-center gap-1.5 mt-0.5">
                <span class="text-xs"></span>
                <button
                  class="btn btn-ghost btn-xs min-h-0 h-6 px-1.5"
                  @click="
                    closeUserMenu();
                    openNewsFeed()
                  "
                >
                  Nouveautés
                </button>
              </div>
            </div>
            <div class="px-2 py-1.5">
              <button
                class="btn btn-ghost btn-xs min-h-0 h-7 w-full justify-start gap-1.5"
                @click="
                    closeUserMenu();
                    openPreferences()
                  "
              >
                <Cog8ToothIcon class="w-3.5 h-3.5" />
                Préférences
              </button>
            </div>
            <div class="px-2 py-1.5">
              <button
                class="btn btn-ghost btn-xs min-h-0 h-7 w-full justify-start gap-1.5 text-error"
                @click="
                  closeUserMenu();
                  logout()
                "
              >
                <ArrowLeftStartOnRectangleIcon class="w-3.5 h-3.5" />
                Déconnexion
              </button>
            </div>
          </div>
        </Teleport>
      </div>
    </ul>

    <Teleport to="body">
      <button
        @click="navBarStore.toggleDrawer()"
        class="drawer-toggle-btn btn btn-ghost btn-sm btn-circle hidden lg:flex fixed w-6 h-6 min-h-0 z-9999 shadow-md border border-base-300 bg-base-100 hover:bg-base-200"
        aria-label="Toggle menu"
        :style="{ left: toggleBtnPos.left + 'px', bottom: toggleBtnPos.bottom + 'px', transform: 'translate(-50%, 0)' }"
      >
        <component v-if="!drawerOpen" :is="ArrowRightIcon" class="w-3 h-3" />
        <component v-else :is="ArrowLeftIcon" class="w-3 h-3" />
      </button>
    </Teleport>
  </div>
  <MobileBottomNavigation />
  <UserPreferenceModal :show-modal="openModalPreferences" @close="closeModalPreferences" />
</template>

<style>
/* Ensure the tooltip container itself doesn't clip */
.tooltip {
  overflow: visible !important;
}

/* Target the DaisyUI tooltip pseudo-elements specifically */
.menu .tooltip-right:before {
  font-size: 0.688rem;
  left: 87% !important;
  z-index: 100000 !important;
  visibility: visible !important;
  transform: translateY(-50%) !important;
  top: 50% !important;
}

/* If DaisyUI uses an arrow (:after), ensure it's also handled */
.menu .tooltip-right:after {
  z-index: 100000 !important;
  left: 75% !important;
}

/* Custom Scrollbar hiding */
.hide-scrollbar {
  -ms-overflow-style: none;
  scrollbar-width: none;
}

.hide-scrollbar::-webkit-scrollbar {
  display: none;
}
</style>
